/*
 * Configuration values needed in js codes
 *
 * NOTE:
 * Please use the following command to avoid accidentally committing personal changes
 * git update-index --assume-unchanged packages/shared/src/utils/config.js
 *
 */

export const livechat_license_id = 12049137;
export const livechat_client_id = '66aa088aad5a414484c1fd1fa8a5ace7';

export const domain_app_ids = {
    // these domains as supported "production domains"
    'deriv.app': 16929, // TODO: [app-link-refactor] - Remove backwards compatibility for `deriv.app`
    'app.deriv.com': 16929,
    'staging-app.deriv.com': 16303,
    'app.deriv.me': 1411,
    'staging-app.deriv.me': 1411, // TODO: setup staging for deriv.me
    'app.deriv.be': 30767,
    'staging-app.deriv.be': 31186,
    'binary.com': 1,
    'test-app.deriv.com': 51072,
};

export const platform_app_ids = {
    derivgo: 23789,
};

export const getCurrentProductionDomain = () =>
    !/^staging\./.test(window.location.hostname) &&
    Object.keys(domain_app_ids).find(domain => window.location.hostname === domain);

export const isProduction = () => {
    const all_domains = Object.keys(domain_app_ids).map(domain => `(www\\.)?${domain.replace('.', '\\.')}`);
    return new RegExp(`^(${all_domains.join('|')})$`, 'i').test(window.location.hostname);
};

export const isTestLink = () => {
    return /^((.*)\.binary\.sx)$/i.test(window.location.hostname);
};

export const isLocal = () => /localhost(:\d+)?$/i.test(window.location.hostname);

const STORAGE_KEY = 'config.app_id';

// Hard-coded app IDs for Botblitz, selected by domain
const DEV_APP_ID = 109139;
const PROD_APP_ID = 109214;

// Dev domains that should use the dev app id
const DEV_DOMAINS = ['dev.botblitz.site', 'dev-bot.botblitz.site', 'dev-binary.botblitz.site'];

const getAppIdForDomain = (): number => {
    const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
    return DEV_DOMAINS.includes(hostname) ? DEV_APP_ID : PROD_APP_ID;
};

/**
 * Resolve app_id from current domain once and store in localStorage.
 * Only updates localStorage when the value is missing or different (e.g. user switched domain).
 * Call once at app bootstrap (lowest level); rest of the app reads from localStorage via getAppId().
 */
export const syncAppIdFromDomain = (): void => {
    if (typeof window === 'undefined') return;
    const resolved = String(getAppIdForDomain());
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored !== resolved) {
            localStorage.setItem(STORAGE_KEY, resolved);
        }
    } catch {
        // ignore
    }
};

/**
 * Returns app_id from localStorage (set once on page load by syncAppIdFromDomain).
 * If storage is empty (e.g. before sync ran), runs sync once then returns.
 * @deprecated Please use 'WebSocketUtils.getAppId' from '@deriv-com/utils' instead of this.
 */
export const getAppId = (): string => {
    if (typeof window === 'undefined') return String(PROD_APP_ID);
    try {
        const stored = (localStorage.getItem(STORAGE_KEY) ?? '').trim();
        if (stored) return stored;
    } catch {
        // ignore
    }
    syncAppIdFromDomain();
    return (localStorage.getItem(STORAGE_KEY) ?? '').trim() || String(getAppIdForDomain());
};

/**
 * Returns the Deriv Bot base URL for the current app id (dev vs prod).
 * DEV_APP_ID -> https://dev-bot.botblitz.site, PROD_APP_ID -> https://bbot.botblitz.site.
 */
export const getBotBaseUrl = (): string => {
    const app_id = getAppId();
    return app_id === String(DEV_APP_ID) ? 'https://dev-bot.botblitz.site' : 'https://bbot.botblitz.site';
};

// Same as root: single production WebSocket URL (ws.derivws.com), optional localStorage override
export const getSocketURL = (_is_wallets = false) => {
    const local_storage_server_url = window.localStorage.getItem('config.server_url');
    if (local_storage_server_url) return local_storage_server_url;
    return 'ws.derivws.com';
};

export const checkAndSetEndpointFromUrl = () => {
    if (isTestLink()) {
        const url_params = new URLSearchParams(location.search.slice(1));

        if (url_params.has('qa_server')) {
            const qa_server = url_params.get('qa_server') || '';

            url_params.delete('qa_server');

            if (/^(^(www\.)?qa[0-9]{1,4}\.deriv.dev|(.*)\.derivws\.com)$/.test(qa_server)) {
                localStorage.setItem('config.server_url', qa_server);
            }

            const params = url_params.toString();
            const hash = location.hash;

            location.href = `${location.protocol}//${location.hostname}${location.pathname}${
                params ? `?${params}` : ''
            }${hash || ''}`;

            return true;
        }
    }

    return false;
};

export const getDebugServiceWorker = () => {
    const debug_service_worker_flag = window.localStorage.getItem('debug_service_worker');
    if (debug_service_worker_flag) return !!parseInt(debug_service_worker_flag);

    return false;
};
